import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { css } from '@emotion/react';
import { Button } from 'antd';
import { InfoCircleOutlined } from 'antd/icons';
import useUser from '@vl-core/hooks/useUser';
import { useRuntimeConfig } from 'vl-core/useRuntimeConfig';
import useDebounceValue from 'vl-common/src/hooks/useDebounceValue';
import formatDate from 'vl-common/src/lib/formatDate';
import useAppointmentDelays from '@hooks/useAppointmentDelays';
import useCancelledNotification from '@hooks/useCancelledNotification';
import useAppointmentCountdown from '@hooks/useAppointmentCountdown';
import useAppointmentStatus from '@hooks/useAppointmentStatus';
import useCallStatusUpdaters from '@hooks/useCallStatusUpdaters';
import { CallStatusTextEnum } from '@hooks/useVoiceCalling';
import { useSessionAppointment } from '@hooks/useSessionAppointment';
import useCallStatus from '@hooks/useCallStatus';
import { Lobby, RingerStopReason } from '@lib/Lobby';
import { useMissedVideoCall } from '@hooks/useMissedVideoCall';
import { PatientStatus } from '@components/Clinicians/Session/Components/PatientStatus';
import { useDPACheck } from '@hooks/useDPACheck';

const RenderTime = ({ counter }: { counter: number }) => {
  const minutes = String(Math.floor(counter / 60)).padStart(2, '0');
  const seconds = String(counter % 60).padStart(2, '0');

  if (Math.floor(counter / 60) >= 0) {
    return (
      <>
        {minutes}:{seconds}{' '}
      </>
    );
  }

  return <>00:00</>;
};

const SessionPatientSummary = ({ callText, lobby }: { callText: string; lobby?: Lobby }) => {
  const { t } = useTranslation('common');
  const { appointment } = useSessionAppointment();
  const { makeDPACheckRequired } = useDPACheck();

  const { delayInMinutes } = useAppointmentDelays(appointment);
  const [cancelled] = useCancelledNotification(appointment);
  const counter = useAppointmentCountdown(appointment, { delayInMinutes });

  const appointmentStart = formatDate(appointment.start, '24 hour time - 09:47');
  const delayDescription = delayInMinutes ? `(${delayInMinutes} min delayed) ` : '';
  const appointmentDescription = cancelled ? '(cancelled) ' : delayDescription;

  const callStatus = useCallStatus(appointment);
  const missed = useMissedVideoCall(appointment);
  const { VIDEO_START_BUFFER } = useRuntimeConfig();
  const user = useUser();
  const isUsersAppointment = appointment.clinician_user_guid === user.user_guid;

  const {
    callStatus: { timestamp: callStatusLastChangedAt, reason },
    base: { pat_restricted: isVideoCallPatientRestricted },
    controlEvents
  } = useAppointmentStatus(appointment);
  const { stopCalling, dial } = useCallStatusUpdaters(appointment);

  const [ringerStopReason, setRingerStopReason] = useState<RingerStopReason>('');

  useEffect(() => {
    if (!lobby) return () => {};

    lobby.on('ringerStopReason', setRingerStopReason);
    return () => lobby.off('ringerStopReason', setRingerStopReason);
  }, [lobby]);

  useEffect(() => {
    if (callStatus === 'calling') {
      const cancelCallTimeout = setTimeout(async () => stopCalling('timeout'), 30_000);
      return () => {
        clearTimeout(cancelCallTimeout);
      };
    }
    return () => {};
  }, [callStatus, stopCalling]);

  const patientStatus = controlEvents.filter(({ control }) => control?.partyStatus && control?.iam === 'patient');
  const patientStatusLength = useDebounceValue(patientStatus.length, 1000);

  // Whilst I am trying to initiate the call with WebSockets (which the patient can stop by declining the call)
  // if there is any activity on the patient side, restart the dialling
  useEffect(() => {
    if (lobby) return;
    if (callStatus === 'calling') {
      // patient activity, send out the dialling event again ...
      dial().then();
      makeDPACheckRequired();
    }
  }, [patientStatusLength, callStatus, lobby]);

  if (callStatus === 'completed') {
    const endDate = callStatusLastChangedAt;

    return (
      <div
        css={css`
          text-align: center;
        `}
      >
        {t('Video call for ')}
        {appointmentStart} {t(' completed on ')}
        {formatDate(endDate, 'Date @ Time - 03/01/2022, 08:57')}
      </div>
    );
  }

  function HasStarted() {
    return (
      <div
        css={css`
          font-weight: 500;
          font-size: 18px;
          color: var(--productGray);
        `}
      >
        {isUsersAppointment ? t('Your') : `${appointment.clinician}'s`} {appointmentStart}
        {t(' appointment has started')}
      </div>
    );
  }

  function WillStart() {
    const start = `${appointmentDescription}${appointmentStart}`;

    if (counter <= 0) {
      return (
        <div
          css={css`
            font-weight: 500;
            font-size: 18px;
            color: var(--productGray);
          `}
        >
          {isUsersAppointment ? t('Your') : `${appointment.clinician}'s`} {start} {t(' appointment is due now')}
        </div>
      );
    }

    return (
      <>
        <div
          css={css`
            font-weight: 500;
            font-size: 18px;
            color: var(--productGray);
          `}
        >
          {isUsersAppointment ? t('Your') : `${appointment.clinician}'s`} {start}
          {t(' appointment is due to start:')}{' '}
        </div>
        {Math.floor(counter / 60) <= 20 && (
          <span
            css={css`
              font-size: 30px;
              letter-spacing: 0;
              color: var(--productGray);
              opacity: 1;
            `}
          >
            <RenderTime counter={counter} />
          </span>
        )}
      </>
    );
  }

  const CallStatusAlert = () => {
    const typeOfCall = appointment.call_type_code;

    if (
      (typeOfCall === 'VIDEO' &&
        callStatus !== 'calling' &&
        reason !== 'UNANSWERED' &&
        reason !== 'USRDECLINED' &&
        !ringerStopReason) ||
      (typeOfCall === 'AUDIO' && !callText)
    ) {
      return <></>;
    }

    let text = CallStatusTextEnum.CALLING;

    if (lobby?.lastRingerStopReason) {
      text = { cancelled: '', declined: CallStatusTextEnum.DECLINED, timeout: CallStatusTextEnum.UNANSWERED }[
        lobby.lastRingerStopReason
      ];
    } else if (typeOfCall === 'VIDEO' && (reason === 'UNANSWERED' || ringerStopReason === 'timeout')) {
      text = CallStatusTextEnum.UNANSWERED;
    } else if (typeOfCall === 'VIDEO' && (reason === 'USRDECLINED' || ringerStopReason === 'declined')) {
      text = CallStatusTextEnum.DECLINED;
    } else if (typeOfCall === 'AUDIO') {
      text = callText;
    }

    return (
      <div
        css={css`
          display: flex;
          width: 100%;
          justify-content: center;

          .calling-alert {
            width: 70%;
            padding: 10px;
            border-radius: 10px;

            &.calling {
              background-color: grey;
            }

            &.not-started {
              background-color: var(--productRed);
            }

            @media (max-width: 768px) {
              width: 90%;
            }
          }

          .calling-alert-text {
            font-size: 24px;
            font-weight: 500;
            margin-bottom: 0;
            color: white;
            @media (max-width: 768px) {
              font-size: 18px;
            }
          }
        `}
      >
        <div className={`calling-alert ${text === CallStatusTextEnum.CALLING ? 'calling' : 'not-started'}`}>
          <span className="calling-alert-text">{text}</span>
        </div>
      </div>
    );
  };

  const StopCallingButton = () => {
    if (appointment.call_type_code === 'VIDEO' && callStatus === 'calling') {
      return (
        <Button
          size="large"
          css={css`
            width: 70%;
            border-radius: 10px;
            color: red;
            background-color: white;
            border: 2px solid red;
            @media (max-width: 768px) {
              width: 90%;
            }
          `}
          onClick={() => stopCalling('')}
          data-testid="stop-calling-button"
        >
          {t('Stop calling')}
        </Button>
      );
    }

    return <></>;
  };

  return (
    <div
      css={css`
        display: flex;
        flex-direction: column;
        align-items: center;
        gap: 0.5rem;
        text-align: center;

        .status {
          font-size: 14px;
          span {
            color: var(--productRed) !important;
          }
        }
      `}
    >
      {!missed && (
        <>
          {callStatus === 'in-progress' ? <HasStarted /> : <WillStart />}
          <CallStatusAlert />
          <StopCallingButton data-testid="stop-calling-button" />
          <div className="status">
            {t('Patient status:')}{' '}
            {isVideoCallPatientRestricted && (
              <>
                <span>{t('restricted')}</span>/
              </>
            )}
            <PatientStatus />
          </div>
          <div className="status">
            {t('Call status: ')}
            {callStatus}
          </div>
          {VIDEO_START_BUFFER && (
            <div className="status">
              <InfoCircleOutlined /> {t('You can call up to ')}
              {VIDEO_START_BUFFER} {t(' minutes before the appointment start time')}{' '}
            </div>
          )}
        </>
      )}
      {missed && (
        <div
          css={css`
            text-align: center;
          `}
        >
          {t('Video call for ')}
          {appointmentStart} {t('missed.')}
        </div>
      )}
    </div>
  );
};

export default SessionPatientSummary;
